// Estilos globales y de librerías
// Eliminamos las importaciones de PDF Viewer
import "react-phone-number-input/style.css";
import "react-toastify/dist/ReactToastify.css";
import "../styles/globals.css";

// Dependencias de React y Next.js
import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
// Proveedores y componentes de terceros
import { UserProvider, useUser } from "@auth0/nextjs-auth0/client";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { ToastContainer, toast } from "react-toastify";

// Componentes y contextos propios
import LoadingSpinner from "../Components/LoadingSpinner";
import SplashScreen from "../Components/SplashScreen";
import { AppWrapper } from "../context/state";
import { Button } from "../Components/Common/Button";
import PushNotifications from "../Components/PushNotifications";


// Integraciones de monitoreo y análisis


// Constantes de configuración
const AMPLITUDE_API_KEY = "dee622db9fc22974a61a156d496d9851";

// Inicialización de terceros - diferida para no bloquear renderizado
// Hook para controlar el estado de carga en la navegación
function useRouteLoading() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleStart = () => setIsLoading(true);
    const handleComplete = () => setIsLoading(false);

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router.events]);

  return isLoading;
}


// Hook para inicializar Amplitude y su plugin de Session Replay
function useAmplitudeInitialization() {
  const initialized = useRef(false);

  useEffect(() => {
    // Esperar a que la página esté completamente cargada e interactiva
    const onLoad = () => {
      // Usar requestIdleCallback si está disponible, o setTimeout como fallback
      const scheduleInit = window.requestIdleCallback || ((cb) => setTimeout(cb, 5000));
      
      scheduleInit(async () => {
        if (!initialized.current) {
          try {
            const amplitude = await import('@amplitude/analytics-browser');
            const { sessionReplayPlugin } = await import('@amplitude/plugin-session-replay-browser');
            
            const sessionReplay = sessionReplayPlugin({
              serverZone: "EU",
            });
            
            amplitude.add(sessionReplay);
            amplitude.init(AMPLITUDE_API_KEY, {
              autocapture: true,
              serverZone: "EU",
            });
            
            initialized.current = true;
            // console.log("Session Replay & Amplitude initialized");
          } catch (error) {
            console.error('Error al inicializar Amplitude:', error);
          }
        }
      });
    };

    // Esperar a que la página termine de cargarse antes de inicializar
    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);
}

/* ============================
   COMPONENTE DE INICIALIZACIÓN
   ============================ */

function UserInitializer() {
  const { user, isLoading } = useUser();
  const identified = useRef(false);

  useEffect(() => {
    if (!isLoading && user && !identified.current) {
      // Usar un timeout para no bloquear la interactividad
      setTimeout(async () => {
        try {
          // Cargar Amplitude solo cuando necesitemos identificar al usuario
          const amplitude = await import('@amplitude/analytics-browser');
          
          // Configuración e identificación en Amplitude
          amplitude.setUserId(user.sub);
          const identifyEvent = new amplitude.Identify()
            .set("email", user.email)
            .set("name", user.name)
            .set("role", user.user_roles?.[0] || "none")
            .set("company", user.user_metadata?.companyName || "none");
  
          amplitude.identify(identifyEvent);
          identified.current = true; // Evita re-identificación
  
          // console.log("Usuario identificado correctamente.");
        } catch (error) {
          console.error('Error al identificar usuario:', error);
        }
      }, 2000);
    }
  }, [user, isLoading]);

  return null; // Este componente no renderiza nada
}

/* ============================
   COMPONENTE PRINCIPAL (APP)
   ============================ */

const Plaved = ({ Component, pageProps }) => {
  const isLoading = useRouteLoading();
  const [showSplash, setShowSplash] = useState(true);

  // Inicialización de Amplitude y registro del Service Worker
  useAmplitudeInitialization();

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  return (
    <React.Fragment>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      
      <UserProvider>
        <UserInitializer />

          <AppWrapper>
            <Head>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
              />
            </Head>

            {isLoading && <LoadingSpinner />}

            <ToastContainer
              position="top-left"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />

            <Component {...pageProps} />
            <PushNotifications />
            <SpeedInsights />
          </AppWrapper>
      </UserProvider>
    </React.Fragment>
  );
};

export default Plaved;